import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Share2 } from 'lucide-react';
import { useShareLink } from '@/features/reel/ShareCard';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

// SHARE THIS TOOL (owner 2026-10-19: "we should add a share link on all tools and tool promos for people to share the
// links directly to chats and social and have the links contain og images that will catch eyes"). One row on every
// tool page and promo: the tool's own link (its preview card is the page's og:image, lib/page-meta.js), with the
// sharer's referral code when they are signed in. Every button says what it does (Studio Blueprint 01).

const TARGETS = [
  { id: 'whatsapp', label: 'WhatsApp', href: (u: string, text: string) => `https://wa.me/?text=${encodeURIComponent(`${text} ${u}`)}` },
  { id: 'facebook', label: 'Facebook', href: (u: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: 'x', label: 'X', href: (u: string, text: string) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(text)}` },
  { id: 'telegram', label: 'Telegram', href: (u: string, text: string) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(text)}` },
] as const;

export function ShareToolBar({ path, text, className, align = 'start' }: { path: string; text?: string; className?: string; align?: 'start' | 'center' }) {
  const { t } = useTranslation();
  const link = useShareLink('quick', { path });
  const words = text || t('shareTool.text');
  const [copied, setCopied] = React.useState(false);
  const [canShare, setCanShare] = React.useState(false);
  React.useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch { /* no clipboard: the link is still in the buttons */ }
    trackEvent('tool_shared', { path, to: 'copy' });
  };
  const native = async () => {
    try {
      await navigator.share({ url: link, text: words });
      trackEvent('tool_shared', { path, to: 'native' });
    } catch { /* closed */ }
  };
  const pill = 'inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-[13.5px] font-semibold text-foreground transition-colors hover:border-primary/50';
  return (
    <div className={cn('flex flex-col gap-2', align === 'center' ? 'items-center' : 'items-start', className)} data-testid="share-tool">
      <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t('shareTool.title')}</p>
      <div className={cn('flex flex-wrap gap-2', align === 'center' && 'justify-center')}>
        {canShare && (
          <button type="button" onClick={() => void native()} className={cn(pill, 'border-primary/50 bg-primary/10')}>
            <Share2 className="h-4 w-4" aria-hidden /> {t('shareTool.share')}
          </button>
        )}
        {TARGETS.map((x) => (
          <a
            key={x.id}
            href={x.href(link, words)}
            target="_blank"
            rel="noopener noreferrer"
            className={pill}
            translate="no"
            onClick={() => trackEvent('tool_shared', { path, to: x.id })}
          >
            {x.label}
          </a>
        ))}
        <button type="button" onClick={() => void copy()} className={pill}>
          {copied ? <Check className="h-4 w-4 text-accent" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
          {copied ? t('shareTool.copied') : t('shareTool.copy')}
        </button>
      </div>
    </div>
  );
}
